import { useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { parseJwt } from "../../utils/jwt";
import { API_BASE_URL, CLIENT_ID, REDIRECT_URI } from "../../auth/config";
import {
  useSetupDispatch,
  setClientInfo,
  setCompany,
  setUser,
} from "../../auth/SetupContext";
import { setAuth } from "../../store/actions";
import { scheduleRefresh } from "../../auth/tokenRefresh";
import { POST_SETUP_FLAG } from "../../auth/postSetupFlag";
import { getDefaultDashboardPath } from "../../utils/userRole";
import styles from "./callback.module.scss";

// TODO: remove once the backend includes userDetail in the /oauth2/token
// response â€” used only when that field is missing so downstream pages
// (AdminDashboard's loginUser guard, Tasks' companyId/userId) keep working.

export default function Callback() {
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();
  const reduxDispatch = useDispatch();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthError = params.get("error");
    const errCompanyId = params.get("companyId");
    const errUserId = params.get("userId");
    const errUsername = params.get("username");
    const verifier = localStorage.getItem("pkce_code_verifier");
    // Must match the client_id/redirect_uri actually used to build the
    // authorize request (stashed by redirectToAuthorize) - the setup wizard
    // can register/use a client_id that differs from the CLIENT_ID env
    // default, and exchanging the code with the wrong one always fails.
    const usedClientId = localStorage.getItem("pkce_client_id") || CLIENT_ID;
    const usedRedirectUri =
      localStorage.getItem("pkce_redirect_uri") || REDIRECT_URI;

    // Routes into the exact wizard step needed to fix the reported problem,
    // instead of always restarting the whole setup flow from step 1.
    const goToSetup = (errorCode) => {
      // The setup wizard was just completed and immediately redirected here
      // to log in - bouncing back into the wizard on any error (e.g. a
      // slow-to-propagate backend record) is confusing and disallowed. Land
      // on the login page with an explanation instead, no matter the error.
      if (sessionStorage.getItem(POST_SETUP_FLAG)) {
        sessionStorage.removeItem(POST_SETUP_FLAG);
        navigate("/login/sso", {
          replace: true,
          state: {
            loginError:
              "Setup finished, but login right after didn't go through. Please try Login again.",
          },
        });
        return;
      }

      dispatch(setClientInfo(CLIENT_ID, REDIRECT_URI));

      switch (errorCode) {
        // Client isn't registered with the SSO server at all yet.
        case "invalid_request":
          navigate("/setup/client", { replace: true });
          break;

        // 403 - client has no access to this company yet. The user already
        // exists (that's how the backend found a companyId at all), so also
        // stash userId/username - GrantAccess uses its presence to skip
        // straight to GrantUserAccess instead of the fresh-setup roles step.
        case "no_company_access":
        case "403":
          if (errCompanyId) dispatch(setCompany({ id: Number(errCompanyId) }));
          if (errUserId)
            dispatch(setUser(Number(errUserId), errUsername || ""));
          navigate("/setup/access", { replace: true });
          break;

        // 401 - company has access, but this user doesn't
        case "no_user_access":
        case "401":
          if (errCompanyId) dispatch(setCompany({ id: Number(errCompanyId) }));
          if (errUserId)
            dispatch(setUser(Number(errUserId), errUsername || ""));
          navigate("/setup/user-access", { replace: true });
          break;

        // 404 - user (and likely company) doesn't exist yet -> full setup
        // from company creation onward (client is already registered)
        case "user_not_found":
        case "404":
        default:
          navigate("/setup/company", { replace: true });
          break;
      }
    };

    if (code) {
      // Exchanged server-side (not a direct POST to /oauth2/token from the
      // browser) - kriha-teams is now a confidential client, and only the
      // backend holds its secret. See AuthController#exchangeToken.
      axios
        .post(`${API_BASE_URL}/api/auth/exchange-token`, {
          client_id: usedClientId,
          code,
          code_verifier: verifier || "",
          redirect_uri: usedRedirectUri,
        })
        .then((res) => {
          const accessToken = res.data.access_token;
          const idToken = res.data.id_token;
          // Not present in the current backend response - once it's added,
          // this flow picks it up automatically with no further changes.
          const refreshToken = res.data.refresh_token || null;
          const expiresIn = res.data.expires_in;
          const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;

          const decodedAccess = parseJwt(accessToken);
          const decodedId = parseJwt(idToken);

          const accessPayload = decodedAccess?.payload || {};
          const idPayload = decodedId?.payload || {};
          const userFromAccess = accessPayload.users || accessPayload.user;
          const userFromId = idPayload.users || idPayload.user;
          const usersObj =
            typeof userFromAccess === "object" && userFromAccess !== null
              ? userFromAccess
              : typeof userFromId === "object" && userFromId !== null
                ? userFromId
                : {};

          // Explicitly extract roles from token payload (e.g. roles: ['Manufacturer'] or ['Brand'])
          const rawRoles =
            accessPayload.roles || idPayload.roles || usersObj.roles || [];
          const roles = Array.isArray(rawRoles)
            ? rawRoles
            : rawRoles
              ? [rawRoles]
              : [];
          const role =
            roles.length > 0
              ? typeof roles[0] === "string"
                ? roles[0]
                : roles[0]?.name
              : usersObj.role || "";

          const companyId =
            accessPayload.companyId ??
            idPayload.companyId ??
            usersObj.companyId ??
            null;
          const userId =
            accessPayload.userId ?? idPayload.userId ?? usersObj.id ?? null;
          const username =
            usersObj.username || usersObj.name || accessPayload.sub || "";
          const email =
            accessPayload.email || usersObj.email || accessPayload.sub || "";
          const mobileNumber =
            accessPayload.mobileNumber || usersObj.mobileNumber || "";

          const loginUser = {
            ...idPayload,
            ...accessPayload,
            ...usersObj,
            id: userId,
            userId,
            companyId,
            roles,
            role,
            username,
            name: username,
            email,
            mobileNumber,
          };

          reduxDispatch(
            setAuth({
              accessToken,
              idToken,
              companyId: companyId ?? null,
              loginUser,
              refreshToken,
              expiresAt,
            }),
          );

          // No-op until the backend starts returning refresh_token/expires_in.
          scheduleRefresh(expiresAt);

          // Self-heals logout for any origin this app is ever served from
          // (new port in dev, a new deploy domain, ...) - the auth server
          // only allows redirecting back to origins it already knows about
          // for this client, so make sure the CURRENT origin is registered
          // before the user can hit logout. Idempotent server-side (backed
          // by a Set) and fire-and-forget so a failure here never blocks
          // login.
          axios
            .post(
              `${API_BASE_URL}/api/oauth/client/post-logout-redirect-uri`,
              null,
              {
                params: {
                  clientId: usedClientId,
                  uri: `${window.location.origin}/`,
                },
              },
            )
            .catch((err) =>
              console.warn(
                "post-logout-redirect-uri self-register failed:",
                err,
              ),
            );

          localStorage.removeItem("pkce_code_verifier");
          localStorage.removeItem("pkce_client_id");
          localStorage.removeItem("pkce_redirect_uri");
          sessionStorage.removeItem(POST_SETUP_FLAG);

          const destination = getDefaultDashboardPath(loginUser);
          console.log("Navigating to destination based on role:", destination);
          navigate("/splash", { replace: true, state: { destination } });
        })
        .catch((err) => {
          console.error("Token exchange failed:", err);
          localStorage.removeItem("pkce_client_id");
          localStorage.removeItem("pkce_redirect_uri");
          goToSetup(err.response?.data?.error);
        });
    } else if (oauthError) {
      console.error(
        "OAuth authorize failed:",
        oauthError,
        params.get("error_description"),
      );
      localStorage.removeItem("pkce_client_id");
      localStorage.removeItem("pkce_redirect_uri");
      goToSetup(oauthError);
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate, dispatch, reduxDispatch]);

  return (
    <div className={styles.page}>
      <div className={styles.text}>Signing you in...</div>
    </div>
  );
}
